import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, CreditCard, Loader2, Plus, Save, X } from 'lucide-react';
import api from '../utils/api';
import { EmptyState, InlineAlert, LoadingState, PageHeader, SectionCard } from '../components/ui';
import { useToast } from '../components/ToastProvider';

export default function CustomerPlansManager() {
  const { id } = useParams();
  const toast = useToast();
  const [plans, setPlans] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    duration_days: 30,
    max_uses_per_month: 0,
    allowed_services: []
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [plansRes, servicesRes] = await Promise.all([
        api.get(`/barbershops/${id}/customer-plans/manage`),
        api.get(`/barbershops/${id}/services`)
      ]);
      setPlans(plansRes.data.plans || []);
      setMemberships(plansRes.data.memberships || []);
      setServices(servicesRes.data.services || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const toggleService = (serviceId) => {
    setForm(prev => ({
      ...prev,
      allowed_services: prev.allowed_services.includes(serviceId)
        ? prev.allowed_services.filter(item => item !== serviceId)
        : [...prev.allowed_services, serviceId]
    }));
  };

  const createPlan = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post(`/barbershops/${id}/customer-plans`, {
        ...form,
        price: Number(form.price),
        duration_days: Number(form.duration_days || 30),
        max_uses_per_month: Number(form.max_uses_per_month || 0)
      });
      setForm({ name: '', description: '', price: '', duration_days: 30, max_uses_per_month: 0, allowed_services: [] });
      setCreating(false);
      setSuccess('Plano criado com sucesso');
      toast.success('Plano criado com sucesso.');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar plano');
    } finally {
      setSaving(false);
    }
  };

  const disablePlan = async (planId) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.delete(`/barbershops/${id}/customer-plans/${planId}`);
      setPlans(current => current.map(plan => plan.id === planId ? response.data.plan : plan));
      setSuccess('Plano desativado');
      toast.success('Plano desativado.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao desativar plano');
    } finally {
      setSaving(false);
    }
  };

  const activatePlan = async (planId) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.put(`/barbershops/${id}/customer-plans/${planId}`, { active: true });
      setPlans(current => current.map(plan => plan.id === planId ? response.data.plan : plan));
      setSuccess('Plano ativado');
      toast.success('Plano ativado.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao ativar plano');
    } finally {
      setSaving(false);
    }
  };

  const togglePlanStatus = async (plan) => {
    if (plan.active) {
      await disablePlan(plan.id);
    } else {
      await activatePlan(plan.id);
    }
  };

  if (loading) {
    return <LoadingState label="Carregando planos..." />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Receita recorrente"
        title="Planos de clientes"
        description="Venda planos mensais para clientes agendarem usando uma assinatura ativa."
        icon={CreditCard}
        action={
          <>
          <button onClick={loadData} className="btn-secondary">Atualizar</button>
          <button onClick={() => setCreating(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Novo plano
          </button>
          </>
        }
      />

      {error && <InlineAlert type="error">{error}</InlineAlert>}
      {success && <InlineAlert type="success">{success}</InlineAlert>}

      {creating && (
        <form onSubmit={createPlan} className="card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Criar plano mensal</h2>
            <button type="button" onClick={() => setCreating(false)} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">Nome do plano</label>
              <input className="input" required placeholder="Ex: Corte mensal" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="label">Valor mensal</label>
              <input className="input" required type="number" min="1" step="0.01" placeholder="79.90" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <label className="label">Duracao em dias</label>
              <input className="input" type="number" min="1" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} />
            </div>
            <div>
              <label className="label">Limite de usos no periodo</label>
              <input className="input" type="number" min="0" value={form.max_uses_per_month} onChange={(e) => setForm({ ...form, max_uses_per_month: e.target.value })} />
              <p className="text-xs text-gray-500 mt-1">Use 0 para ilimitado.</p>
            </div>
          </div>

          <div>
            <label className="label">Descricao</label>
            <textarea className="input min-h-[90px]" placeholder="Ex: Cliente pode agendar corte durante o mes." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>

          <div>
            <label className="label">Servicos inclusos</label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {services.map(service => (
                <label key={service.id} className="flex items-center gap-3 rounded-lg border border-dark-700 bg-dark-800 px-3 py-2 cursor-pointer">
                  <input type="checkbox" checked={form.allowed_services.includes(service.id)} onChange={() => toggleService(service.id)} />
                  <span className="text-sm text-gray-200">{service.nome}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">Se nenhum for marcado, o plano vale para qualquer servico.</p>
          </div>

          <button className="btn-primary" disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar plano
          </button>
        </form>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        {plans.length === 0 ? (
          <EmptyState
            className="lg:col-span-3"
            icon={CreditCard}
            title="Nenhum plano criado"
            description="Crie o primeiro plano mensal para seus clientes e comece a vender recorrencia."
            action={<button onClick={() => setCreating(true)} className="btn-primary"><Plus className="w-4 h-4 mr-2" />Novo plano</button>}
          />
        ) : plans.map(plan => (
          <div key={plan.id} className="card p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-white">{plan.name}</h2>
                <p className="text-sm text-gray-400 mt-1">{plan.description || 'Plano mensal de cliente'}</p>
              </div>
              <span className={`rounded-full border px-2 py-1 text-xs ${plan.active ? 'border-green-500/20 bg-green-500/10 text-green-300' : 'border-red-500/20 bg-red-500/10 text-red-300'}`}>{plan.active ? 'Ativo' : 'Desativado'}</span>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">R$ {Number(plan.price || 0).toFixed(2)}</p>
              <p className="text-xs text-gray-500">{plan.duration_days} dias - {plan.max_uses_per_month ? `${plan.max_uses_per_month} usos` : 'uso ilimitado'}</p>
            </div>
            <button onClick={() => togglePlanStatus(plan)} disabled={saving} className={plan.active ? 'btn-secondary w-full' : 'btn-primary w-full'}>{plan.active ? 'Desativar' : 'Ativar novamente'}</button>
          </div>
        ))}
      </div>

      <SectionCard title="Assinaturas recentes" description="Clientes com assinatura criada e uso registrado." icon={CheckCircle}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-gray-400">
              <tr>
                <th className="text-left py-3">Cliente</th>
                <th className="text-left py-3">Plano</th>
                <th className="text-left py-3">Status</th>
                <th className="text-left py-3">Usos</th>
                <th className="text-left py-3">Validade</th>
              </tr>
            </thead>
            <tbody>
              {memberships.map(item => (
                <tr key={item.id} className="border-t border-dark-700">
                  <td className="py-3 text-white">{item.customer_name}<p className="text-xs text-gray-500">{item.customer_phone}</p></td>
                  <td className="py-3 text-gray-300">{item.plan_name}</td>
                  <td className="py-3"><span className="inline-flex items-center gap-1 text-primary-300"><CheckCircle className="w-3 h-3" />{item.status}</span></td>
                  <td className="py-3 text-gray-300">{item.uses_count}</td>
                  <td className="py-3 text-gray-300">{item.ends_at ? new Date(item.ends_at).toLocaleDateString('pt-BR') : '-'}</td>
                </tr>
              ))}
              {memberships.length === 0 && (
                <tr><td colSpan="5" className="py-6 text-center text-gray-500">Nenhuma assinatura ainda.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
